/** SEO metadata and routing for public calculator landing pages. */

export const PUBLIC_CALCULATOR_SLUGS = [
  "incometax",
  "homeloan",
  "retirement",
  "insurance",
  "emergency",
  "pf",
  "ppf",
  "sip",
] as const;

export type PublicCalculatorSlug = (typeof PUBLIC_CALCULATOR_SLUGS)[number];

export interface CalculatorSeoEntry {
  slug: PublicCalculatorSlug;
  /** Visible page H1 (exactly one per calculator landing). */
  h1: string;
  title: string;
  description: string;
  keywords: string;
}

export const CALCULATOR_SEO: Record<PublicCalculatorSlug, CalculatorSeoEntry> = {
  incometax: {
    slug: "incometax",
    h1: "Income Tax Calculator",
    title: "Income Tax Calculator 2026 - New vs Old Regime | TellerWand",
    description:
      "Free income tax calculator for India. Compare old vs new tax regime and estimate liability for FY 2026-27. Try now.",
    keywords:
      "income tax calculator India, FY 2026-27 tax calculator, old vs new tax regime, tax planning India, TellerWand",
  },
  homeloan: {
    slug: "homeloan",
    h1: "Home Loan EMI Calculator",
    title: "Home Loan EMI Calculator - Calculate EMI in India | TellerWand",
    description:
      "Calculate home loan EMI, total interest, and repayment schedule. Free home loan calculator for Indian borrowers.",
    keywords:
      "home loan EMI calculator India, housing loan calculator, EMI calculator, mortgage calculator India",
  },
  retirement: {
    slug: "retirement",
    h1: "Retirement Calculator",
    title: "Retirement Calculator - How Much To Save? | TellerWand",
    description:
      "Plan your retirement corpus and monthly SIP requirements. Free retirement calculator built for Indian investors.",
    keywords:
      "retirement calculator India, retirement corpus calculator, SIP for retirement, pension planning India",
  },
  insurance: {
    slug: "insurance",
    h1: "Insurance Calculator",
    title: "Insurance Calculator - Term & Life Cover | TellerWand",
    description:
      "Estimate life and health insurance coverage for your family. Free insurance needs calculator for Indian households.",
    keywords:
      "insurance calculator India, life insurance coverage calculator, health insurance calculator, term insurance",
  },
  emergency: {
    slug: "emergency",
    h1: "Emergency Fund Calculator",
    title: "Emergency Fund Calculator - How Much To Save? | TellerWand",
    description:
      "Calculate the ideal emergency fund for financial security. Free emergency fund calculator for Indian savers.",
    keywords:
      "emergency fund calculator India, financial safety net, savings buffer calculator, rainy day fund",
  },
  pf: {
    slug: "pf",
    h1: "PF Calculator",
    title: "PF Calculator - EPF Maturity Calculator | TellerWand",
    description:
      "Calculate Provident Fund (EPF) corpus and withdrawal projections. Free PF calculator for salaried employees in India.",
    keywords:
      "PF calculator India, EPF calculator, provident fund calculator, EPF maturity calculator",
  },
  ppf: {
    slug: "ppf",
    h1: "PPF Calculator",
    title: "PPF Calculator - Calculate PPF Maturity in 2026 | TellerWand",
    description:
      "Free PPF calculator for India. Calculate maturity amount, interest and tax benefits with current PPF rates. Try now.",
    keywords:
      "PPF calculator India, PPF maturity calculator, PPF returns calculator, Section 80C PPF, tax saving calculator",
  },
  sip: {
    slug: "sip",
    h1: "SIP Calculator",
    title: "SIP Calculator - Calculate SIP Returns | TellerWand",
    description:
      "Calculate SIP returns and plan mutual fund investments. Free SIP calculator with projected wealth for Indian investors.",
    keywords:
      "SIP calculator India, mutual fund SIP calculator, SIP returns calculator, investment calculator India",
  },
};

export function isPublicCalculatorSlug(value: string): value is PublicCalculatorSlug {
  return (PUBLIC_CALCULATOR_SLUGS as readonly string[]).includes(value);
}

export function getCalculatorSeo(slug: string): CalculatorSeoEntry | undefined {
  if (!isPublicCalculatorSlug(slug)) {
    return undefined;
  }
  return CALCULATOR_SEO[slug];
}

/** Maps public URL slug to the internal calculator id used by calculator components. */
export function calculatorSlugToId(slug: PublicCalculatorSlug): string {
  return slug;
}
